import React, { useState, useEffect } from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  SelectChangeEvent
} from '@mui/material';
import { StatusMap } from '@utils/status-options.utils.ts';
import { AvailabilityStatus } from '@models/global.model.ts';
import { useTranslation } from 'react-i18next';

export const StatusSelect: React.FC<{
  value: AvailabilityStatus;
  rowId: number;
  onStatusUpdate: (id: number, status: string) => void;
  optionsList: any;
}> = ({ value, rowId, onStatusUpdate, optionsList }) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<AvailabilityStatus>(value);

  const currentStatus = StatusMap[selectedValue];
  const currentOption = optionsList.find(
    (option: any) => option.value === selectedValue
  ) || {
    id: selectedValue,
    title: selectedValue.charAt(0).toUpperCase() + selectedValue.slice(1),
    value: selectedValue
  };

  const isDisabled =
    selectedValue === 'canceled' || selectedValue === 'rejected';

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value as AvailabilityStatus;

    if (isDisabled) return;

    if (newValue && newValue !== selectedValue) {
      setSelectedValue(newValue);
      onStatusUpdate(rowId, newValue);
    }
  };

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const isValueInOptions = optionsList.some(
    (option: any) => option.value === selectedValue
  );

  return (
    <Tooltip
      title={
        selectedValue === 'canceled'
          ? t('canceled_status_text')
          : selectedValue === 'rejected'
            ? t('rejected_status_text')
            : ''
      }
      disableHoverListener={!isDisabled}
    >
      <FormControl
        disabled={isDisabled}
        size="small"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        sx={{
          minWidth: 120,
          width: 150,
          backgroundColor: currentStatus?.color
            ? `color-mix(in srgb, ${currentStatus.color} 12%, transparent)`
            : 'var(--gray-10)',
          borderRadius: '6px',
          '& .MuiSelect-select': {
            color: currentStatus?.color || 'inherit',
            fontWeight: 500,
            py: 0.5,
            px: 1.5,
            cursor: isDisabled ? 'not-allowed !important' : 'pointer'
          },
          '& .MuiOutlinedInput-root.MuiSelect-root.Mui-disabled .MuiSelect-select':
            {
              WebkitTextFillColor: `${currentStatus?.color || 'inherit'} !important`,
              opacity: 1
            },
          '& fieldset': { border: 'none' }
        }}
      >
        <Select
          value={isValueInOptions ? selectedValue : ''}
          onChange={handleChange}
          displayEmpty
          disabled={isDisabled}
          renderValue={() => (
            <span style={{ color: currentStatus?.color || 'inherit' }}>
              {currentOption.title}
            </span>
          )}
          sx={{
            '& .MuiSelect-icon': {
              stroke: currentStatus?.color || 'inherit'
            }
          }}
        >
          {optionsList.map((option: any) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                color:
                  StatusMap[option.value as AvailabilityStatus]?.color ||
                  'inherit',
                fontWeight: 500
              }}
            >
              {option.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  );
};

export default StatusSelect;
